import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BASE_PATH } from '../lib';
import { FormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import {
  InteractionType,
  PublicClientApplication,
  LogLevel,
  BrowserCacheLocation,
  IPublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';
import { StudentSearchComponent } from './student-search/student-search.component';
import { LoginActionComponent } from './login-action/login-action.component';
import { StudentListComponent } from './student-list/student-list.component';
import { AppConfigService } from './app-config.service';
import { StudentDetailComponent } from './student-detail/student-detail.component';
import { GenesysCloudInterceptor } from './genesys-cloud.interceptor';
import { DialpadComponent } from './dialpad/dialpad.component';
import { ErrorHandlerService } from './error-handler.service';
import { AppInsightsService } from './app-insights.service';
import { ChatComponent } from './sms/chat/chat.component';
import { ChatResponsesComponent } from './sms/chat-responses/chat-responses.component';
import { ChatToolPlaceholderComponent } from './sms/chat-tool-placeholder/chat-tool-placeholder.component';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { GenesysLoginService } from './login-action/gensys-auth.guard';
import { GenesysLoginComponent } from './genesys-login/genesys-login.component';
import { AzureLoginComponent } from './azure-login/azure-login.component';
import { ConsultTransferComponent } from './consult-transfer/consult-transfer.component';
import { ChatFilterComponent } from './sms/chat-filter/chat-filter.component';
import { AdmissionUiComponent } from './admission-ui/admission-ui.component';
import { DispositionScreenComponent } from './admission-ui/disposition-screen/disposition-screen.component';
import { DNCScreenComponent } from './admission-ui/dncscreen/dncscreen.component';
import { MultipleMatchScreenComponent } from './admission-ui/multiple-match-screen/multiple-match-screen.component';
import { ScheduleScreenComponent } from './admission-ui/schedule-screen/schedule-screen.component';
import { LoadingScreenComponent } from './admission-ui/loading-screen/loading-screen.component';
import { StudentInfoBlockComponent } from './admission-ui/student-info-block/student-info-block.component';
import { ConfirmationDialogComponent } from './admission-ui/confirmation-dialog/confirmation-dialog.component';
import { LeadConversationStateStore } from './dialpad/leadConversation.state';
import { DialpadService } from './shared/dialpad.service';
import { ConversationUtils } from './shared/conversation.utils';
import { TimeFormatPipe } from './admission-ui/time.format.pipe';
import { CommonModule } from '@angular/common';
import { IdleScreenComponent } from './admission-ui/idle-screen/idle-screen.component';
import { DateFormatterUtils } from './shared/dateformatter.utils';
import { TelemetryInterceptor } from './interceptors/telemetry.interceptor';
import { CustomRetryInterceptor } from './interceptors/custom-retry.interceptor';

export function MSALInterceptorConfigFactory(
  appConfigService: AppConfigService
): MsalInterceptorConfiguration {
  const protectedResources = appConfigService?.config?.protectedResources;
  const protectedResourceMap = new Map<string, Array<string>>();
  if (protectedResources?.endpoint && protectedResources.scopes) {
    protectedResourceMap.set(
      protectedResources.endpoint,
      protectedResources.scopes
    );
  }
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

export function MSALInstanceFactory(
  appConfigService: AppConfigService
): IPublicClientApplication {
  const msalConfig = appConfigService.config?.msal;
  return new PublicClientApplication({
    auth: {
      clientId: msalConfig?.auth.clientId || '',
      authority: msalConfig?.auth.authority || '',
      redirectUri: msalConfig?.auth.redirectUri,
      postLogoutRedirectUri: msalConfig?.auth.postLogoutRedirectUri,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: true,
      },
      allowRedirectInIframe: true,
      windowHashTimeout: msalConfig?.system?.windowHashTimeout ?? 10000, // Applies just to popup calls - In milliseconds
      iframeHashTimeout: msalConfig?.system?.iframeHashTimeout ?? 10000, // Applies just to silent calls - In milliseconds
      loadFrameTimeout: msalConfig?.system?.loadFrameTimeout ?? 10000, // Applies to both silent and popup calls - In milliseconds
    },
  });
}

export function MSALGuardConfigFactory(
  appConfigService: AppConfigService
): MsalGuardConfiguration {
  const protectedResources = appConfigService?.config?.protectedResources;
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: protectedResources?.scopes,
    },
    loginFailedRoute: '',
  };
}

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

@NgModule({
  declarations: [
    TimeFormatPipe,
    AppComponent,
    StudentListComponent,
    StudentSearchComponent,
    LoginActionComponent,
    StudentDetailComponent,
    DialpadComponent,
    GenesysLoginComponent,
    AzureLoginComponent,
    ChatComponent,
    ChatResponsesComponent,
    ChatToolPlaceholderComponent,
    ConsultTransferComponent,
    ChatFilterComponent,
    AdmissionUiComponent,
    DispositionScreenComponent,
    DNCScreenComponent,
    MultipleMatchScreenComponent,
    ScheduleScreenComponent,
    LoadingScreenComponent,
    StudentInfoBlockComponent,
    ConfirmationDialogComponent,
    IdleScreenComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    NgbModule,
    FormsModule,
    MsalModule,
    OAuthModule.forRoot(),
    MatDialogModule,
    CommonModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (appConfigService: AppConfigService, insights: AppInsightsService) => () =>
        appConfigService.loadConfigToService().then(() => insights.init()),
      deps: [AppConfigService, AppInsightsService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: 'APP_CONFIG',
      useFactory: (appConfigService: AppConfigService) =>
        appConfigService.config,
      deps: [AppConfigService],
    },
    {
      provide: BASE_PATH,
      useFactory: (appConfigService: AppConfigService) =>
        appConfigService.config?.apiConfig?.basePath,
      deps: [AppConfigService],
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppConfigService],
    },
    MsalGuard,
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [AppConfigService],
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [AppConfigService],
    },
    MsalService,
    { provide: OAuthStorage, useValue: localStorage },
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GenesysCloudInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomRetryInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TelemetryInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    AppInsightsService,
    LeadConversationStateStore,
    DialpadService,
    ConversationUtils,
    DateFormatterUtils
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
